import * as signalR from "@microsoft/signalr";
import { useEffect, useState } from "react";
import config from "@/config";
import { getCookie } from "../generic.methods";
import { setSignalRConnectionStatus } from "@/store/slices/general/signalRHubSlice";
import { useDispatch } from "react-redux";

const HUB_KEY = "frontdoor-notifications";
const isWindow = typeof window !== "undefined";

/**
 * useFrontdoorSignalR
 *
 * A dedicated SignalR hook for the FrontDoor notification hub.
 * Connects to config.frontdoorSignalRUrl with the same auth scheme
 * as the existing CRM hubs (CRM-Auth bearer + query params).
 *
 * Returns { hub, connected } so consumers can call hub.on() / hub.invoke().
 */
const useFrontdoorSignalR = () => {
  const [connected, setConnected] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isWindow) return;

    const isPublicPage = window.location.pathname.includes("book-an-appointment") ||
      window.location.pathname.includes("reschedule-an-appointment") ||
      window.location.pathname.includes("cancel-an-appointment");

    if (isPublicPage) {
      return;
    }

    const frontdoorUrl = config.frontdoorSignalRUrl;
    if (!frontdoorUrl) {
      console.warn("[FrontdoorSignalR] NEXT_PUBLIC_FRONTDOOR_SIGNALR_URL is not set, skipping connection.");
      return;
    }

    // Avoid duplicate connections
    if (window.hubs?.[HUB_KEY]) {
      setConnected(!!window.hubStatuses?.[HUB_KEY]);
      return;
    }

    const token = getCookie("CRM-Auth") || "";
    const companyKey = getCookie("companyKey") || "";
    const companyName = getCookie("companyName") || "";
    const legacyAuth = getCookie("Auth") || "";

    if (!companyKey) {
      return;
    }

    // Build connection URL with query params (same scheme as existing hubs)
    let signalUrl = `${frontdoorUrl}?companyKey=${companyKey}`;
    if (companyName) {
      signalUrl += `&companyName=${companyName}`;
    }
    if (legacyAuth) {
      signalUrl += `&apexMainAuthToken=${legacyAuth}`;
    }

    // Initialize global hub containers if needed
    if (!window.hubs) window.hubs = {};
    if (!window.hubStatuses) window.hubStatuses = {};

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build();

    window.hubs[HUB_KEY] = connection;

    connection
      .start()
      .then(() => {
        console.log("[FrontdoorSignalR] Connection established");
        window.hubStatuses[HUB_KEY] = true;
        setConnected(true);
        dispatch(setSignalRConnectionStatus({ hubName: HUB_KEY, status: true }));
      })
      .catch((error) => {
        console.error("[FrontdoorSignalR] Connection failed:", error);
        window.hubStatuses[HUB_KEY] = false;
        setConnected(false);
      });

    connection.onreconnected((connectionId) => {
      console.log("[FrontdoorSignalR] Reconnected:", connectionId);
      window.hubStatuses[HUB_KEY] = true;
      setConnected(true);
      dispatch(setSignalRConnectionStatus({ hubName: HUB_KEY, status: true }));
    });

    connection.onreconnecting((err) => {
      console.log("[FrontdoorSignalR] Reconnecting...", err);
      dispatch(setSignalRConnectionStatus({ hubName: HUB_KEY, status: false }));
      setConnected(false);
    });

    connection.onclose(() => {
      console.log("[FrontdoorSignalR] Connection closed");
      window.hubStatuses[HUB_KEY] = false;
      setConnected(false);
      dispatch(setSignalRConnectionStatus({ hubName: HUB_KEY, status: false }));
    });
  }, []);

  return {
    hub: isWindow ? window.hubs?.[HUB_KEY] : null,
    connected,
    hubKey: HUB_KEY,
  };
};

export default useFrontdoorSignalR;
